import { Icon } from '@buggy/shared'
import { Divider, Flex, Skeleton } from 'antd'
import { FunctionComponent, ReactNode } from 'react'
import Highlighter from 'react-highlight-words'
import styled, { useTheme } from 'styled-components'
import { SafeNewTabLink } from '@/components/safe-new-tab-link'
import { useThemeConstants } from '@/hooks/use-theme-constants'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { ampli, VisitKolSocialMediaProperties } from '@/utils/ampli'
import { PlatformShortcode } from '@/utils/convert-platform'

interface KolPlatformInfoProps {
  searchWords?: string[]
  kolInfo?: KolSchemaModel
  platformShortcode?: PlatformShortcode
  isFetching?: boolean
  isClickedFromDrawer?: boolean
  className?: string
  from?: VisitKolSocialMediaProperties['from']
}

const KolPlatformLink: FunctionComponent<KolPlatformInfoProps> = ({
  kolInfo,
  searchWords = [],
  isFetching,
  platformShortcode,
  isClickedFromDrawer,
  from,
  className,
}) => {
  const theme = useTheme()
  const { highlightStyle } = useThemeConstants()
  const link = kolInfo?.links.find((link) => link.type === platformShortcode)

  if (!link) {
    return null
  }

  return (
    <Wrapper className={className}>
      {((): ReactNode => {
        if (isFetching) {
          return <Skeleton active paragraph={{ rows: 2 }} title={false} />
        }
        return (
          <>
            <Flex
              align='center'
              gap={4}
              style={{ height: 20, color: theme.colors.text.secondary }}
            >
              <Highlighter
                autoEscape
                highlightStyle={highlightStyle}
                searchWords={searchWords}
                textToHighlight={(link.nameList ?? []).join(' | ')}
              />
            </Flex>
            <SafeNewTabLink
              href={link.permanentURL}
              onClick={(): void => {
                ampli.visitKolSocialMedia({
                  platform: platformShortcode,
                  from,
                  isClickedFromDrawer,
                })
              }}
            >
              <Flex align='center' gap={4}>
                <Icon
                  color={theme.colors.brand.primary}
                  fontSize={16}
                  name='link'
                />
                <SocialLinkText>{link.permanentURL}</SocialLinkText>
              </Flex>
            </SafeNewTabLink>
          </>
        )
      })()}
      <Divider />
    </Wrapper>
  )
}

export default styled(KolPlatformLink)``

const SocialLinkText = styled.div`
  color: ${({ theme }): string => theme.colors.brand.primary};
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  text-decoration-line: underline;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`
